import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, Users, FileText, Calendar, DollarSign, TrendingUp, 
  Settings, LogOut, Menu, X, CheckSquare, MessageSquare,
  Globe, BarChart3, UserCircle, HelpCircle, Briefcase,
  CreditCard, Bell, Mail, UsersRound
} from 'lucide-react';

const IndependentLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/independent/login');
  };

  const menuItems = [
    { path: '/independent/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/independent/clients', icon: Users, label: 'Clients' },
    { path: '/independent/leads', icon: Briefcase, label: 'Leads' },
    { path: '/independent/quotes', icon: FileText, label: 'Quotes' },
    { path: '/independent/itineraries', icon: Calendar, label: 'Itineraries' },
    { path: '/independent/payments', icon: DollarSign, label: 'Payments' },
    { path: '/independent/payment-links', icon: CreditCard, label: 'Payment Links' },
    { path: '/independent/payment-reminders', icon: Bell, label: 'Payment Reminders' },
    { path: '/independent/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/independent/workflow', icon: TrendingUp, label: 'Workflow' },
    { path: '/independent/communications', icon: MessageSquare, label: 'Communications' },
    { path: '/independent/groups', icon: UsersRound, label: 'Group Bookings' },
    { path: '/independent/website', icon: Globe, label: 'My Website' },
    { path: '/independent/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/independent/commissions', icon: DollarSign, label: 'Commissions' },
    { path: '/independent/profile', icon: UserCircle, label: 'Profile' },
    { path: '/independent/support', icon: HelpCircle, label: 'Support' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`bg-blue-900 text-white transition-all duration-300 flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-blue-800 flex-shrink-0">
          {sidebarOpen && <h1 className="text-xl font-bold">Travel+ CRM</h1>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded hover:bg-blue-800"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-4 py-3 hover:bg-blue-800 transition-colors ${
                isActive(item.path) ? 'bg-blue-800 border-l-4 border-white' : ''
              }`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span className="ml-3">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="border-t border-blue-800 p-4 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 hover:bg-blue-800 rounded transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>

        {/* Footer */}
        {sidebarOpen && (
          <div className="border-t border-blue-800 p-3 text-center flex-shrink-0">
            <p className="text-xs text-blue-300">© 2026 Travel+ CRM</p>
            <p className="text-xs text-blue-400 mt-1">v1.0.0</p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              Independent Agent Portal
            </h2>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-700">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500">Independent Agent</p>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default IndependentLayout;
